import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';

import styles from '@components/carePlan/CarePlan.module.css';
import CarePlanGoals from '@components/carePlan/CarePlanGoals';
import CarePlanPinned from '@components/carePlan/CarePlanPinned';
import CarePlanServices from '@components/carePlan/CarePlanServices';
import ColumnGrid from '@components/layout/ColumnGrid';
import Heading2 from '@components/typography/Heading1';
import useMe from '@hooks/useMe';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useMyClient from '@hooks/useMyClient';
import {
  GetJourneyWithPlanQuery,
  GetPinsQuery,
  JourneyMarkReadDocument,
  JourneyMarkReadMutation,
  JourneyMarkReadMutationVariables
} from '@typing/Generated';

type Journey = GetJourneyWithPlanQuery['journey'];

type Props = {
  journey: Journey;
  pins: GetPinsQuery['pins'];
};

const CarePlan = ({ journey, pins }: Props) => {
  const myClient = useMyClient();
  const me = useMe();
  const [read, setRead] = useState(false);
  const [firstTime, setFirstTime] = useState(false);
  const location = useLocation();

  const goalsRef = useRef<HTMLDivElement>(null);
  const scheduleConsultRef = useRef<HTMLDivElement>(null);
  const expertServicesRef = useRef<HTMLDivElement>(null);
  const toolsRef = useRef<HTMLDivElement>(null);
  const learnRef = useRef<HTMLDivElement>(null);
  const connectRef = useRef<HTMLDivElement>(null);

  const [markReadJourneyCarePlan] = useMutationWithValidation<
    JourneyMarkReadMutation,
    JourneyMarkReadMutationVariables
  >(JourneyMarkReadDocument, 'carePlan');

  useEffect(() => {
    let scrollToRef: React.MutableRefObject<HTMLDivElement | null> | null = null;

    if (location.hash.includes('next-steps')) {
      scrollToRef = goalsRef;
    } else if (location.hash.includes('goals')) {
      scrollToRef = goalsRef;
    } else if (location.hash.includes('schedule-consult')) {
      scrollToRef = scheduleConsultRef;
    } else if (location.hash.includes('expert-services')) {
      scrollToRef = expertServicesRef;
    } else if (location.hash.includes('tools')) {
      scrollToRef = toolsRef;
    } else if (location.hash.includes('learn')) {
      scrollToRef = learnRef;
    } else if (location.hash.includes('peer-support')) {
      scrollToRef = connectRef;
    }

    let timeout: NodeJS.Timeout | null = null;

    if (scrollToRef) {
      timeout = setTimeout(() => scrollToRef?.current?.scrollIntoView({ behavior: 'smooth' }), 700);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  useEffect(() => {
    if (!read && myClient) {
      if (journey.ownerViewedAt === null) {
        setFirstTime(true);
      }

      markReadJourneyCarePlan({
        id: journey.id
      });
      setRead(true);
    }
  }, [journey.clientRoles, journey.id, journey.ownerViewedAt, markReadJourneyCarePlan, myClient, myClient?.id, read]);

  return (
    <div>
      {myClient && (
        <div className={styles.welcome}>
          <Heading2>
            <FormattedMessage
              id={firstTime ? 'pages.carePlan.base.welcome' : 'pages.carePlan.base.welcomeBack'}
              values={{ firstName: me?.firstName }}
            />
          </Heading2>
        </div>
      )}

      <CarePlanGoals journey={journey} />
      <ColumnGrid columns={2} focusSide="left">
        <CarePlanServices journey={journey} />
        <div>
          <CarePlanPinned journey={journey} pins={pins} />
        </div>
      </ColumnGrid>
    </div>
  );
};

export default CarePlan;
